import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';

export const useGetBuyerInventories = (page: number, size: number) => {
  const buyerInventoriesQuery = graphql(`
    query buyerInventory($page: Int!, $size: Int!) {
      buyerInventory(pageInput: { page: $page, size: $size }) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          quantity {
            value
            unit
          }
          inboundStock
          location {
            id
            name
            city
            region
          }
          platformProductsLength {
            id
            platformProduct {
              id
              code
              name
              properties {
                species {
                  code
                  name
                }
              }
            }
            length {
              displayValue
            }
          }
          buyerInventoryConfiguration {
            id
            usageRatePerWeek {
              value
              unit
            }
            weeksCoveredBeforeUrgent
            weeksCoveredBeforeMonitor
            targetWeeksCovered
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    buyerInventoriesQuery,
    ['buyerInventories', page.toString(), size.toString()],
    {
      page: page,
      size: size,
    }
  );
};

export const useGetBuyerInventoriesForLocation = (
  locationId: string,
  page: number,
  size: number
) => {
  const buyerInventoriesForLocationQuery = graphql(`
    query buyerInventoryForLocation(
      $locationId: ID!
      $page: Int!
      $size: Int!
    ) {
      buyerInventoryForLocation(
        locationId: $locationId
        pageInput: { page: $page, size: $size }
      ) {
        paging {
          totalElements
          totalPages
          number
          size
        }
        content {
          id
          quantity {
            value
            unit
          }
          inboundStock
          location {
            id
            name
            city
            region
          }
          platformProductsLength {
            id
            platformProduct {
              id
              code
              name
              properties {
                species {
                  code
                  name
                }
              }
            }
            length {
              displayValue
            }
          }
          buyerInventoryConfiguration {
            id
            usageRatePerWeek {
              value
              unit
            }
            weeksCoveredBeforeUrgent
            weeksCoveredBeforeMonitor
            targetWeeksCovered
          }
        }
      }
    }
  `);

  return useGraphqlQuery(
    buyerInventoriesForLocationQuery,
    ['buyerInventories', locationId, page.toString(), size.toString()],
    {
      locationId: locationId,
      page: page,
      size: size,
    }
  );
};

export const useUpsertInventory = () => {
  const upsertInventoryMutation = graphql(`
    mutation upsertInventory(
      $platformProductsLengthId: ID!
      $locationId: ID!
      $quantity: QuantityInput!
    ) {
      upsertInventory(
        platformProductsLengthId: $platformProductsLengthId
        locationId: $locationId
        quantity: $quantity
      ) {
        id
        location {
          id
          name
        }
        platformProductsLength {
          id
          platformProduct {
            name
          }
        }
        buyerInventoryConfiguration {
          weeksCoveredBeforeMonitor
          weeksCoveredBeforeUrgent
          targetWeeksCovered
          location {
            id
          }
          platformProductsLength {
            id
          }
          usageRatePerWeek {
            value
            unit
          }
        }
        quantity {
          value
          unit
        }
      }
    }
  `);
  return useGraphqlMutation(upsertInventoryMutation, ['buyerInventories']);
};

export const useGetPlatformProductsLengthsForProduct = (
  platformProductId: string
) => {
  const platformProductsLengthsForProductQuery = graphql(`
    query platformProductsLengthsForProduct($platformProductId: ID!) {
      platformProductsLengthsForProduct(platformProductId: $platformProductId) {
        id
        platformProduct {
          id
          name
        }
        length {
          id
          displayValue
        }
      }
    }
  `);
  return useGraphqlQuery(
    platformProductsLengthsForProductQuery,
    ['buyerInventories', platformProductId],
    {
      platformProductId: platformProductId,
    }
  );
};
export const useGetSellerProductInventoriesForBuyer = (
  platformProductLengthId?: string | null,
  platformProductId?: string | null
) => {
  const sellerProductInventoriesForBuyerQuery = graphql(`
    query sellerProductInventoriesForBuyer(
      $platformProductLengthId: ID
      $platformProductId: ID
    ) {
      sellerProductInventoriesForBuyer(
        filter: {
          platformProductLengthId: $platformProductLengthId
          platformProductId: $platformProductId
        }
      ) {
        id
        quantity {
          value
          unit
        }
        organization {
          id
          name
        }
        platformProductsLength {
          length {
            displayValue
          }
          platformProduct {
            name
            code
            id
          }
          id
        }
        sellerProductConfiguration {
          id
          currencyCode
          pricePerUnit
          minimumOrderQuantity {
            value
            unit
          }
          readyToShipDays
        }
      }
    }
  `);
  return useGraphqlQuery(
    sellerProductInventoriesForBuyerQuery,
    [
      'sellerProductInventories',
      platformProductLengthId ?? 'none',
      platformProductId ?? 'none',
    ],
    {
      platformProductLengthId: platformProductLengthId,
      platformProductId: platformProductId,
    }
  );
};

export const useGetBuyerProductInventory = (id: string) => {
  const inventoryQuery = graphql(`
    query buyerProductInventory($id: ID!) {
      buyerProductInventory(id: $id) {
        id
        quantity {
          value
          unit
        }
        location {
          id
          name
          city
          region
        }
        platformProductsLength {
          id
          platformProduct {
            id
            name
            code
            actualThicknessInches
            actualWidthInches
            finishCode
            constructionCode
            properties {
              construction {
                code
                name
              }
              species {
                code
                name
              }
              grade {
                code
                name
              }
              dryness {
                code
                name
              }
              surfacing {
                code
                name
              }
            }
          }
          length {
            displayValue
          }
        }
        buyerInventoryConfiguration {
          id
          usageRatePerWeek {
            value
            unit
          }
          weeksCoveredBeforeUrgent
          weeksCoveredBeforeMonitor
          targetWeeksCovered
        }
      }
    }
  `);

  return useGraphqlQuery(inventoryQuery, ['inventory', id], {
    id: id,
  });
};
